import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getThemePreference, updateThemePreference } from './api';

const ThemeContext = createContext();

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'dark'
      ? {
          // Palette values for dark mode
          primary: {
            main: '#16a6ff',
          },
          secondary: {
            main: '#ddd',
          },
          background: {
            default: '#282c35',
            paper: '#1b1b1c',
          },
          text: {
            primary: '#ddd',
            secondary: '#aaa',
          },
        }
      : {
          // Palette values for light mode
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
          background: {
            default: '#f4f6f8',
            paper: '#ffffff',
          },
          text: {
            primary: '#000000',
            secondary: '#555555',
          },
        }),
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    h1: { fontSize: '2.5rem' },
    h2: { fontSize: '2rem' },
    h3: { fontSize: '1.75rem' },
    body1: { fontSize: '1.125rem' },
    body2: { fontSize: '1rem' },
    button: {
      fontSize: '1.2rem',
      textTransform: 'none',
      fontWeight: '800',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.text.primary}`,
          borderRadius: '12px',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.mode === 'light' ? '#e6f0ff' : '#333',
            borderRadius: 8,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.mode === 'light' ? '#e6f0ff' : 'transparent',
            display: 'inline-block',
            padding: '0 4px',
            borderRadius: 4,
            transform: 'translate(14px, 12px) scale(1)',
            transition: 'all 0.2s ease',
  
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '& fieldset': {
              borderColor: theme.palette.text.secondary,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
          }),
          input: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: ({ theme }) => ({
            backgroundColor: theme.palette.mode === 'light' ? '#e6f0ff' : '#333',
            borderRadius: 8,
            padding: '10px 14px',
            color: theme.palette.text.primary,
            '&:focus': {
              backgroundColor: theme.palette.mode === 'light' ? '#dceeff' : '#444',
            },
          }),
          icon: ({ theme }) => ({
            color: theme.palette.primary.main,
          }),
        },
      },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.inverted-toggle': {
            backgroundColor: 'rgba(22, 166, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(22, 166, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.inverted-icon': {
            color: '#16a6ff',
            fontSize: '1.8rem',
            transition: 'all 0.3s ease',
          },
          '&.inverted-icon-active': {
            color: '#fff',
            backgroundColor: '#16a6ff',
            borderRadius: '50%',
            padding: '4px',
          },
        },
      },
    },
  },
});

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(localStorage.getItem('theme_mode') || 'dark');

  const refreshThemeFromServer = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const { theme_mode } = await getThemePreference();
      if (theme_mode === 'light' || theme_mode === 'dark') {
        setMode(theme_mode);
        localStorage.setItem('theme_mode', theme_mode);
      }
    } catch (err) {
      console.error('Failed to load theme preference from server', err);
    }
  };

  useEffect(() => {
    refreshThemeFromServer();
  }, []);

  const toggleTheme = async () => {
    const nextMode = mode === 'light' ? 'dark' : 'light';
    setMode(nextMode);
    localStorage.setItem('theme_mode', nextMode);

    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      await updateThemePreference(nextMode);
    } catch (err) {
      console.error('Failed to save theme preference to server', err);
    }
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // iOS Safari подкрашивает свои панели по цвету страницы и theme-color: держим html, body
  // и theme-color одного цвета, иначе на iPhone бывают «прямоугольники» цвета фона поверх контента
  useEffect(() => {
    const bg = theme.palette.background.default;
    document.documentElement.style.backgroundColor = bg;
    document.documentElement.style.colorScheme = mode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', bg);
  }, [theme, mode]);

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode, refreshThemeFromServer }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
